import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Container, Box } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASEURL } from '../constants/Common';
import { ThemeProvider } from '@mui/material/styles';
import getTheme from '../theme/theme';

const EmailVerification: React.FC = () => {
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendTimeout, setResendTimeout] = useState(180); // 3 minutes = 180 seconds
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');
    const userraccess = JSON.parse("" + localStorage.getItem('userrole'));

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (resendTimeout > 0) {
            timer = setInterval(() => {
                setResendTimeout((prevTimeout) => prevTimeout - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendTimeout]);

    useEffect(() => {
        if (resendTimeout === 0) {
            setIsResending(false);
        }
    }, [resendTimeout]);


    const formatTime = (time: number) => String(time).padStart(2, '0');

    const minutes = formatTime(Math.floor(resendTimeout / 60));
    const seconds = formatTime(resendTimeout % 60);

    const handleVerification = async () => {
        if (!otp) {
            toast.error('Please enter the OTP.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(
                BASEURL + '/verify-otp/',
                { otp_code: otp },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.status === 200) {
                localStorage.setItem('authTokenlogin', response.data.access);
                const projectAccess = userraccess?.projectaccess;
                if (projectAccess === 'No Access') {
                    toast.success('User login successful');
                    navigate('/dailylogs');
                    window.location.reload();
                } else {
                    toast.success('User login successful');
                    navigate('/projmgmt');
                    window.location.reload();
                }
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 400) {
                const errorMessage = error.response.data.error;
                if (errorMessage === 'Incorrect OTP.') {
                    toast.error('Please check your email and enter OTP correctly.');
                } else if (errorMessage === 'OTP has expired') {
                    toast.error('OTP has expired, click on resend OTP.');
                } else if (errorMessage === 'OTP does not exist') {
                    console.log('OTP does not exist.');
                } else {
                    toast.error('Failed to verify email. Please try again.');
                }
            } else {
                console.log('An error occurred during verification. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setIsResending(true);
        setResendTimeout(180); // Reset the timeout to 3 minutes

        try {
            const response = await axios.post(
                BASEURL + '/resend-otp/',
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.status === 200) {
                toast.success('OTP sent successfully');
            } else {
                toast.error('Failed to resend OTP. Please try again.');
            }
        } catch (error) {
            console.log('An error occurred while resending OTP.');
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        handleVerification();
    };

    return (
        <ThemeProvider theme={getTheme()}>
            <Container component='main' maxWidth='xs'>
                <Box
                    sx={{
                        marginTop: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginLeft: 5,
                        marginRight: 5,
                    }}
                >
                    <Typography variant="body1">
                        Please enter the OTP sent to your email to complete verification
                    </Typography>
                    <Box component='form' noValidate sx={{ mt: 1 }} onSubmit={handleSubmit}>
                        <TextField
                            margin='normal'
                            required
                            fullWidth
                            name='otp'
                            label='OTP'
                            id='otp'
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                        />
                        <Button
                            fullWidth
                            variant='contained'
                            sx={{
                                mt: 1,
                                '&:disabled': { bgcolor: 'grey.400', color: 'grey.700' }
                            }}
                            onClick={handleVerification}
                            disabled={isLoading}
                        >
                            Verify
                        </Button>
                        <Button
                            fullWidth
                            variant='contained'
                            sx={{
                                mt: 3,
                                mb: 2,
                                '&:disabled': { bgcolor: 'grey.400', color: 'grey.700' }
                            }}
                            onClick={handleResendOtp}
                            disabled={isResending || resendTimeout > 0}
                        >
                            {resendTimeout > 0

                                ? `resend otp in ${minutes}:${seconds}`
                                : 'resend otp'}
                        </Button>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
};

export default EmailVerification;

